/**
 * Where the plays are, said plainly, always.
 *
 * BUILD_PLAN 5.8 and the acceptance criteria both put it the same way: never a
 * silent failure. There is no state in which this renders nothing, which is why
 * every branch below returns copy rather than null.
 *
 * The copy follows CLAUDE.md's error rules. It never apologises, it says what
 * happened and what happens next, and it never shows a status code. In every
 * failing state the sentence a coach needs is the same one: the plays are on
 * this phone and they are not lost.
 */

import { Link } from 'react-router';
import { supportCodeLine } from '@good-season/ui';

import type { SyncStatus, SyncTrouble } from './sync.js';
import type { Persistence } from './useScorebook.js';
import { LOST_LINE, UNAVAILABLE_TITLE } from './unavailable.js';

export interface SyncIndicatorProps {
  readonly status: SyncStatus;
  readonly persistence: Persistence;
  readonly onRetry: () => void;
  readonly onReopen?: (() => void) | undefined;
  /**
   * One line, for a screen too short for the full form (the book does not
   * scroll, so something has to give). The same states and the same words: the
   * label and a short reason are shown, the full sentence stays for a screen
   * reader, one button sits on the line, a reference code is on the line in
   * sight and never cut, and the shape that stands for the state is unchanged.
   * The line has ONE height in every state, so a status change never moves what
   * is under it, and the button has its room reserved whether or not there is
   * one. A state offers a single button (Retry, Reopen, or Sign in).
   *
   * What is shortened on this form, always (nothing here measures room): the count
   * is a number and the word "saved", so a coach never reads a bare
   * number as plays still to enter (the full sentence is for a screen reader), and
   * the button is an icon and a short word, still a 44px target, with its full name. The reason and the reference
   * code are never cut. The browser-persistence note is not on this form (the
   * book moves it into its More sheet).
   */
  readonly compact?: boolean | undefined;
}

interface Line {
  readonly tone: 'ok' | 'working' | 'warn';
  readonly label: string;
  /** The number the label starts with, when it has one. A compact line shows just this. */
  readonly count?: number | undefined;
  readonly detail: string;
  /** What a compact line shows beside the label. Empty when the label says it. */
  readonly brief: string;
  /** The reference code, shown beside the brief on the compact line and never cut. */
  readonly reference?: string | undefined;
}

function lineFor(status: SyncStatus): Line {
  switch (status.kind) {
    case 'synced':
      return { tone: 'ok', label: 'Saved', detail: 'Every play is on the server.', brief: '' };
    case 'syncing':
      return {
        tone: 'working',
        label: 'Sending',
        detail: `${String(status.pending)} to go.`,
        brief: `${String(status.pending)} to go`,
      };
    case 'pending':
      return {
        tone: 'working',
        label: `${String(status.pending)} waiting`,
        count: status.pending,
        detail: 'Saved on this phone. Sending shortly.',
        brief: 'Sending soon',
      };
    case 'held':
      return heldLine(status.pending, status.trouble, status.diagnostic);
    case 'superseded':
      // The notice above the book carries the message and the Reload button.
      // This line stays (the indicator never renders nothing) but says only
      // what is true and offers nothing: Try now cannot work until the reload.
      if (status.storeMayBeGone) {
        // The store may have been cleared too, so no claim that sending
        // resumes, only what to do before the reload.
        return { tone: 'warn', label: 'Paused', detail: LOST_LINE, brief: 'Write plays down' };
      }
      return {
        tone: 'warn',
        label: 'Paused',
        detail: 'Sending resumes after the page reloads.',
        brief: 'Reload to resume',
      };
    case 'unavailable':
      // No Try now below: nothing on this phone is left to send, and every
      // later read refuses until the reload.
      return {
        tone: 'warn',
        label: 'Cleared',
        detail: `${UNAVAILABLE_TITLE} ${LOST_LINE}`,
        brief: 'Write plays down',
      };
    case 'broken':
      return {
        tone: 'warn',
        label: 'Not readable',
        detail: 'The plays on this phone could not be read. Close the app and open it again.',
        brief: 'Reopen the app',
      };
  }
}

/** The code a coach reads to support, for the two states that are not self-explanatory. */
function withReference(sentence: string, diagnostic: string | undefined): string {
  return diagnostic === undefined ? sentence : `${sentence} ${supportCodeLine(diagnostic)}`;
}

function heldLine(pending: number, trouble: SyncTrouble, diagnostic: string | undefined): Line {
  const held = `${String(pending)} on this phone`;
  const counted = { label: held, count: pending };
  if (trouble === 'offline') {
    return {
      tone: 'warn',
      ...counted,
      detail: 'No signal. They send when you are back.',
      brief: 'No signal',
    };
  }
  if (trouble === 'finalized') {
    return {
      tone: 'warn',
      ...counted,
      detail: 'This game was finished. Reopen it to send them.',
      // Always said on the one-line form. The closed-book notice only exists on the
      // device that pressed Finish, and a game finished somewhere else (a 409 on
      // the next sync) shows nothing but this line.
      brief: 'Game finished',
    };
  }
  if (trouble === 'signedOut') {
    return {
      tone: 'warn',
      ...counted,
      detail: withReference(
        'You are signed out. Plays are saved on this device. Sign in to send them.',
        diagnostic,
      ),
      brief: 'Signed out',
      reference: diagnostic,
    };
  }
  return {
    tone: 'warn',
    ...counted,
    detail: withReference('The server has not taken them yet. Still trying.', diagnostic),
    brief: 'Still trying',
    reference: diagnostic,
  };
}

/** A small arrow, so the compact button is a shape and a short word, not a long word. */
function ButtonIcon() {
  return (
    <svg aria-hidden="true" className="sb-sync__icon" focusable="false" viewBox="0 0 16 16">
      <path d="M3 8h9M8.5 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function SyncIndicator({
  status,
  persistence,
  onRetry,
  onReopen,
  compact = false,
}: SyncIndicatorProps) {
  const line = lineFor(status);
  const canReopen =
    status.kind === 'held' && status.trouble === 'finalized' && onReopen !== undefined;

  const signedOut = status.kind === 'held' && status.trouble === 'signedOut';

  return (
    <div className={`sb-sync${compact ? ' sb-sync--compact' : ''}`} data-tone={line.tone}>
      {/*
        `role="status"` so a screen reader is told when the book stops getting
        through. A sighted coach glances at the colour; this is the other half
        of the same information, and colour is never the only carrier of it.
      */}
      <p className="sb-sync__line" role="status">
        {/* A shape as well as a colour, per the design system rule. */}
        <span aria-hidden="true" className="sb-sync__dot" />
        <span className="sb-sync__label">
          {compact && line.count !== undefined ? (
            <>
              {/* A number and the one word that says the plays are safe, never the bare number. */}
              <span aria-hidden="true" className="sb-sync__count">
                {line.count} saved
              </span>
              <span className="sb__srOnly">{line.label}</span>
            </>
          ) : (
            line.label
          )}
        </span>
        {compact ? (
          <>
            {line.brief === '' && line.reference === undefined ? null : (
              <span aria-hidden="true" className="sb-sync__detail">
                <span className="sb-sync__reason">{line.brief}</span>
                {line.reference === undefined ? null : (
                  <span className="sb-sync__ref">ref {line.reference}</span>
                )}
              </span>
            )}
            <span className="sb__srOnly">{line.detail}</span>
          </>
        ) : (
          <span className="sb-sync__detail">{line.detail}</span>
        )}
      </p>

      {persistence === 'denied' && !compact ? (
        /*
         * A refusal is NORMAL in a plain tab and granted silently on an
         * installed PWA, so this is quiet rather than an alarm. It is here at
         * all because the consequence is a game evicted mid-season, and
         * installing the app is a real fix a coach can apply.
         */
        <p className="sb-sync__persist">
          This browser has not promised to keep the book. Install Good Season to make it permanent.
        </p>
      ) : null}

      {compact ? (
        /*
         * The one button of the one-line form, in a slot that is there in every
         * state (empty when there is none) so the line is the same height and
         * the same width of text throughout. A finished game offers Reopen (trying
         * again cannot send into a closed book); a signed-out device offers Sign in
         * (trying again cannot send without a session); anything else, Retry now.
         * The names are the same ones the long form gives its buttons.
         */
        <div className="sb-sync__actions">
          {status.kind === 'held' || status.kind === 'broken' ? (
            signedOut ? (
              <Link aria-label="Sign in to send" className="sb-sync__button" to="/signin">
                <ButtonIcon />
                Sign in
              </Link>
            ) : canReopen ? (
              <button
                aria-label="Reopen and send"
                className="sb-sync__button"
                onClick={onReopen}
                type="button"
              >
                <ButtonIcon />
                Reopen
              </button>
            ) : (
              <button
                aria-label="Retry now"
                className="sb-sync__button"
                onClick={onRetry}
                type="button"
              >
                <ButtonIcon />
                Retry
              </button>
            )
          ) : null}
        </div>
      ) : status.kind === 'held' || status.kind === 'broken' ? (
        <div className="sb-sync__actions">
          <button className="sb-sync__button" onClick={onRetry} type="button">
            Retry now
          </button>
          {signedOut ? (
            /*
             * The app's one sign-in route. There is no return-to mechanism to
             * hand it (SignIn lands on /team), so the coach comes back to the
             * book by hand; the plays are in the outbox and wait.
             */
            <Link className="sb-sync__button" to="/signin">
              Sign in to send
            </Link>
          ) : null}
          {canReopen ? (
            /*
             * Not "Reopen game", which is what the tray calls the control for
             * closing and unclosing the book.
             *
             * Both are on screen together on the path that matters most: this
             * device finishes, somebody records one more play, and the sync
             * refusal puts this button up beside the tray's. Two controls with
             * one accessible name is a screen reader user told to press a
             * button there are two of, and it is the reason this button says
             * what it will DO rather than repeating the other one's name.
             */
            <button className="sb-sync__button" onClick={onReopen} type="button">
              Reopen and send
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
